import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { forkJoin, Subscription } from 'rxjs';
import { saveAs } from 'file-saver';
import { TenantScope } from '../../company/tenant-access.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { HrService } from '../hr.service';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';
import { AdminOperationsScopeParams } from './hr-admin-operations.api';
import {
  ADMIN_OPERATION_FREQUENCIES,
  ADMIN_OPERATION_TYPES,
  ADMIN_OPERATION_WORKFLOW_STATES,
  AdminOperationsAuthority,
  AdminOperationsCalendar,
  AdminOperationsCalendarRow,
  AdminOperationsCycle,
  AdminOperationsDashboard,
  AdminOperationsDocument,
  AdminOperationsEvidence,
  AdminOperationsImportPreview,
  AdminOperationsObligation,
  AdminOperationsScope
} from './hr-admin-operations.models';

@Component({
  selector: 'app-hr-admin-operations',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, CheckboxModule, DialogModule, FileUploadModule,
    InputTextModule, ProgressBarModule, SelectModule, TableModule, TabsModule, TagModule,
    TextareaModule, ToastModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent,
    PageDataPanelComponent, HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-admin-operations.component.html',
  styleUrls: ['./hr-admin-operations.component.scss']
})
export class HrAdminOperationsComponent implements OnInit, OnDestroy {
  private readonly hrService = inject(HrService);
  private readonly tenantScopeService = inject(TenantScopeService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly subscriptions = new Subscription();

  loading = false;
  activeTab: string | number = 'overview';
  year = new Date().getFullYear();
  scope: TenantScope | null = null;
  dashboard: AdminOperationsDashboard = {};
  scopes: AdminOperationsScope[] = [];
  authorities: AdminOperationsAuthority[] = [];
  obligations: AdminOperationsObligation[] = [];
  cycles: AdminOperationsCycle[] = [];
  documents: AdminOperationsDocument[] = [];
  calendar: AdminOperationsCalendar = {};
  importPreview?: AdminOperationsImportPreview;

  siteFilter?: number;
  categoryFilter?: string;
  authorityFilter?: number;
  ownerFilter?: number;
  frequencyFilter?: string;
  statusFilter?: string;
  dateFrom?: string;
  dateTo?: string;

  obligationDialogVisible = false;
  obligationForm: AdminOperationsObligation = {};
  documentDialogVisible = false;
  documentForm: AdminOperationsDocument = {};
  authorityDialogVisible = false;
  authorityForm: AdminOperationsAuthority = {};
  scopeDialogVisible = false;
  scopeForm: AdminOperationsScope = {};
  actionDialogVisible = false;
  selectedCycle?: AdminOperationsCycle;
  actionTargetStatus = 'IN_PROGRESS';
  actionNotes = '';
  actionReason = '';
  evidenceForm: AdminOperationsEvidence = {};
  verificationDialogVisible = false;
  selectedDocument?: AdminOperationsDocument;
  verificationStatus = 'VERIFIED';
  verificationReason = '';

  readonly months = [
    { key: 1, label: 'Jan' }, { key: 2, label: 'Feb' }, { key: 3, label: 'Mar' },
    { key: 4, label: 'Apr' }, { key: 5, label: 'May' }, { key: 6, label: 'Jun' },
    { key: 7, label: 'Jul' }, { key: 8, label: 'Aug' }, { key: 9, label: 'Sep' },
    { key: 10, label: 'Oct' }, { key: 11, label: 'Nov' }, { key: 12, label: 'Dec' }
  ];
  readonly frequencyOptions = ADMIN_OPERATION_FREQUENCIES.map(value => ({ label: this.format(value), value }));
  readonly typeOptions = ADMIN_OPERATION_TYPES.map(value => ({ label: this.format(value), value }));
  readonly statusOptions = ADMIN_OPERATION_WORKFLOW_STATES.map(value => ({ label: this.format(value), value }));
  readonly scopeTypeOptions = ['COMPANY', 'BRANCH', 'WAREHOUSE', 'OFFICE', 'FACILITY']
    .map(value => ({ label: this.format(value), value }));
  readonly confidentialityOptions = ['PUBLIC', 'INTERNAL', 'HR_RESTRICTED', 'LEGAL_RESTRICTED']
    .map(value => ({ label: this.format(value), value }));
  readonly verificationOptions = [
    { label: 'Verify as Valid', value: 'VERIFIED' },
    { label: 'Reject', value: 'REJECTED' }
  ];

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.adminOperations.manage);
  readonly canComplete = this.permissionService.hasPermission(app_permissions.hr.adminOperations.complete);
  readonly canVerify = this.permissionService.hasPermission(app_permissions.hr.adminOperations.verify);
  readonly canAct = this.canComplete || this.canVerify;
  readonly canSetup = this.permissionService.hasPermission(app_permissions.hr.adminOperations.setup);
  readonly canImportExport = this.permissionService.hasPermission(app_permissions.hr.adminOperations.importExport);

  ngOnInit(): void {
    this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => {
      this.scope = scope;
      this.load();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get companyMutationId(): number | undefined {
    return this.scope?.type === 'COMPANY' ? this.scope.companyId : undefined;
  }

  get mutationAllowed(): boolean {
    return !!this.companyMutationId;
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Overdue Work', value: this.dashboard.overdueCycles ?? 0, tone: this.riskTone(this.dashboard.overdueCycles) },
      { label: 'Due This Month', value: this.dashboard.dueThisMonth ?? 0, tone: 'warn' },
      { label: 'Expires ≤30 Days', value: this.dashboard.expiring30Days ?? 0, tone: 'danger' },
      { label: 'Expires ≤60 Days', value: this.dashboard.expiring60Days ?? 0, tone: 'warn' },
      { label: 'Expires ≤90 Days', value: this.dashboard.expiring90Days ?? 0, tone: 'info' },
      { label: 'Pending Verification', value: this.dashboard.pendingDocumentVerification ?? 0, tone: 'warn' }
    ];
  }

  get filteredObligations(): AdminOperationsObligation[] {
    return this.obligations.filter(item =>
      (!this.siteFilter || item.scopeId === this.siteFilter)
      && (!this.authorityFilter || item.authorityId === this.authorityFilter)
      && (!this.ownerFilter || item.ownerEmployeeId === this.ownerFilter)
      && (!this.frequencyFilter || item.frequencyCode === this.frequencyFilter));
  }

  get filteredCycles(): AdminOperationsCycle[] {
    return this.cycles.filter(item =>
      (!this.siteFilter || item.scopeId === this.siteFilter)
      && (!this.categoryFilter || item.category === this.categoryFilter)
      && (!this.ownerFilter || item.ownerEmployeeId === this.ownerFilter)
      && (!this.statusFilter || item.workflowStatus === this.statusFilter)
      && (!this.dateFrom || (item.dueDate || '') >= this.dateFrom)
      && (!this.dateTo || (item.dueDate || '') <= this.dateTo));
  }

  get categoryOptions(): { label: string; value: string }[] {
    return [...new Set(this.authorities.map(item => item.category).filter((value): value is string => !!value))]
      .sort().map(value => ({ label: this.format(value), value }));
  }

  get ownerOptions(): { label: string; value: number }[] {
    const owners = new Map<number, string>();
    this.obligations.forEach(item => {
      if (item.ownerEmployeeId) owners.set(item.ownerEmployeeId, item.ownerName || `Employee ${item.ownerEmployeeId}`);
    });
    return [...owners].map(([value, label]) => ({ label, value }));
  }

  load(): void {
    if (!this.scope) return;
    const params = this.scopeParams();
    this.loading = true;
    forkJoin({
      dashboard: this.hrService.adminOperationsDashboard(params),
      scopes: this.hrService.adminOperationsScopes(params),
      authorities: this.hrService.adminOperationsAuthorities(params),
      obligations: this.hrService.adminOperationsObligations(params),
      cycles: this.hrService.adminOperationsCycles(params),
      documents: this.hrService.adminOperationsDocuments(params),
      calendar: this.hrService.adminOperationsCalendar(this.year, params)
    }).subscribe({
      next: response => {
        this.dashboard = response.dashboard.data ?? {};
        this.scopes = response.scopes.data ?? [];
        this.authorities = response.authorities.data ?? [];
        this.obligations = response.obligations.data ?? [];
        this.cycles = response.cycles.data ?? [];
        this.documents = response.documents.data ?? [];
        this.calendar = response.calendar.data ?? {};
      },
      error: () => this.toast('error', 'Unable to load Admin Operations.'),
      complete: () => this.loading = false
    });
  }

  changeYear(delta: number): void {
    this.year += delta;
    this.loading = true;
    this.hrService.adminOperationsCalendar(this.year, this.scopeParams()).subscribe({
      next: response => this.calendar = response.data ?? {},
      error: () => this.toast('error', 'Unable to load the annual calendar.'),
      complete: () => this.loading = false
    });
  }

  clearFilters(): void {
    this.siteFilter = undefined;
    this.categoryFilter = undefined;
    this.authorityFilter = undefined;
    this.ownerFilter = undefined;
    this.frequencyFilter = undefined;
    this.statusFilter = undefined;
    this.dateFrom = undefined;
    this.dateTo = undefined;
  }

  editObligation(item?: AdminOperationsObligation): void {
    if (!this.companyMutationId) return;
    this.obligationForm = item ? { ...item } : {
      owningCompanyId: this.companyMutationId, obligationType: 'PORTAL_VISIT',
      frequencyCode: 'MONTHLY', evidenceRequired: true, approvalRequired: false,
      cycleWarningDaysCsv: '7,3,1,0', documentWarningDaysCsv: '90,60,30,7',
      confidentialityLevel: 'INTERNAL', active: true
    };
    this.obligationDialogVisible = true;
  }

  saveObligation(): void {
    this.loading = true;
    this.hrService.saveAdminOperationsObligation(this.obligationForm).subscribe({
      next: () => {
        this.obligationDialogVisible = false;
        this.toast('success', 'Compliance obligation saved.');
        this.load();
      },
      error: () => {
        this.loading = false;
        this.toast('error', 'Unable to save the obligation.');
      }
    });
  }

  launchAsNeeded(item: AdminOperationsObligation): void {
    if (!item.obligationId) return;
    this.hrService.launchAdminOperationsCycle(item.obligationId).subscribe({
      next: () => {
        this.toast('success', 'As-needed compliance cycle launched.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to launch the as-needed cycle.')
    });
  }

  editDocument(item?: AdminOperationsDocument, renew = false): void {
    if (!this.companyMutationId) return;
    this.documentForm = item && !renew ? { ...item } : {
      owningCompanyId: this.companyMutationId,
      obligationId: item?.obligationId,
      previousDocumentId: renew ? item?.documentId : undefined,
      documentName: item?.documentName,
      documentNumber: item?.documentNumber,
      issuer: item?.issuer,
      confidentialityLevel: item?.confidentialityLevel || 'INTERNAL',
      active: true
    };
    this.documentDialogVisible = true;
  }

  saveDocument(): void {
    this.loading = true;
    this.hrService.saveAdminOperationsDocument(this.documentForm).subscribe({
      next: () => {
        this.documentDialogVisible = false;
        this.toast('success', this.documentForm.previousDocumentId ? 'Renewed document version created.' : 'Document saved.');
        this.load();
      },
      error: () => {
        this.loading = false;
        this.toast('error', 'Unable to save the document.');
      }
    });
  }

  editAuthority(item?: AdminOperationsAuthority): void {
    if (!this.companyMutationId) return;
    this.authorityForm = item ? { ...item } : {
      owningCompanyId: this.companyMutationId, countryCode: 'SA', active: true
    };
    this.authorityDialogVisible = true;
  }

  saveAuthority(): void {
    this.hrService.saveAdminOperationsAuthority(this.authorityForm).subscribe({
      next: () => {
        this.authorityDialogVisible = false;
        this.toast('success', 'Portal directory entry saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save the portal entry.')
    });
  }

  editScope(item?: AdminOperationsScope): void {
    if (!this.companyMutationId) return;
    this.scopeForm = item ? { ...item } : {
      owningCompanyId: this.companyMutationId, linkedCompanyId: this.companyMutationId,
      scopeType: 'COMPANY', active: true
    };
    this.scopeDialogVisible = true;
  }

  saveScope(): void {
    this.hrService.saveAdminOperationsScope(this.scopeForm).subscribe({
      next: () => {
        this.scopeDialogVisible = false;
        this.toast('success', 'Compliance scope saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save the compliance scope.')
    });
  }

  openAction(cycle: AdminOperationsCycle): void {
    if (!cycle.cycleId) return;
    this.hrService.adminOperationsCycle(cycle.cycleId).subscribe({
      next: response => this.prepareAction(response.data ?? cycle),
      error: () => this.toast('error', 'Unable to load cycle evidence and action history.')
    });
  }

  private prepareAction(cycle: AdminOperationsCycle): void {
    this.selectedCycle = cycle;
    this.actionTargetStatus = cycle.workflowStatus === 'PENDING' ? 'IN_PROGRESS'
      : cycle.workflowStatus === 'IN_PROGRESS' ? 'SUBMITTED'
        : cycle.workflowStatus === 'SUBMITTED' ? 'COMPLETED' : 'PENDING';
    this.actionNotes = '';
    this.actionReason = '';
    this.evidenceForm = { evidenceType: 'FILE', confidentialityLevel: 'INTERNAL' };
    this.actionDialogVisible = true;
  }

  addEvidence(): void {
    if (!this.selectedCycle?.cycleId) return;
    this.hrService.addAdminOperationsEvidence(this.selectedCycle.cycleId, this.evidenceForm).subscribe({
      next: response => {
        this.selectedCycle!.evidence = [...(this.selectedCycle!.evidence ?? []), response.data!];
        this.evidenceForm = { evidenceType: 'FILE', confidentialityLevel: 'INTERNAL' };
        this.toast('success', 'Evidence attached.');
      },
      error: () => this.toast('error', 'Unable to attach evidence.')
    });
  }

  transitionCycle(): void {
    if (!this.selectedCycle?.cycleId) return;
    this.hrService.transitionAdminOperationsCycle(this.selectedCycle.cycleId, this.actionTargetStatus,
      this.actionNotes, this.actionReason).subscribe({
      next: () => {
        this.actionDialogVisible = false;
        this.toast('success', `Cycle moved to ${this.format(this.actionTargetStatus)}.`);
        this.load();
      },
      error: () => this.toast('error', 'Transition blocked. Check evidence, approval, or reason requirements.')
    });
  }

  openVerification(document: AdminOperationsDocument): void {
    this.selectedDocument = document;
    this.verificationStatus = 'VERIFIED';
    this.verificationReason = '';
    this.verificationDialogVisible = true;
  }

  verifyDocument(): void {
    if (!this.selectedDocument?.documentId) return;
    this.hrService.verifyAdminOperationsDocument(this.selectedDocument.documentId,
      this.verificationStatus, this.verificationReason).subscribe({
      next: () => {
        this.verificationDialogVisible = false;
        this.toast('success', 'Document verification recorded.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to verify the document.')
    });
  }

  activateTemplate(): void {
    if (!this.companyMutationId) return;
    this.loading = true;
    this.hrService.activateKsaAdminOperationsTemplate(this.companyMutationId, this.year).subscribe({
      next: response => {
        const data = response.data ?? {};
        this.toast('success', `${data['obligationsCreated'] ?? 0} KSA obligations activated.`);
        this.load();
      },
      error: () => {
        this.loading = false;
        this.toast('error', 'Unable to activate the KSA template.');
      }
    });
  }

  evaluate(): void {
    if (!this.companyMutationId) return;
    this.loading = true;
    this.hrService.evaluateAdminOperations(this.companyMutationId).subscribe({
      next: () => {
        this.toast('success', 'Schedules, risk states, tasks, and reminders evaluated.');
        this.load();
      },
      error: () => {
        this.loading = false;
        this.toast('error', 'Unable to evaluate compliance.')
      }
    });
  }

  previewImport(event: FileSelectEvent): void {
    const file = event.files?.[0];
    if (!file || !this.companyMutationId) return;
    this.loading = true;
    this.hrService.previewAdminOperationsImport(this.companyMutationId, file).subscribe({
      next: response => this.importPreview = response.data,
      error: () => this.toast('error', 'Unable to preview the import file.'),
      complete: () => this.loading = false
    });
  }

  commitImport(): void {
    if (!this.companyMutationId || !this.importPreview?.rows?.length || this.importPreview.errorRows) return;
    this.hrService.commitAdminOperationsImport(this.companyMutationId, this.importPreview.rows).subscribe({
      next: response => {
        this.toast('success', `${response.data?.['obligationsCreated'] ?? 0} obligations imported.`);
        this.importPreview = undefined;
        this.load();
      },
      error: () => this.toast('error', 'Import commit failed; no partial rows were applied.')
    });
  }

  exportWorkbook(): void {
    this.hrService.exportAdminOperations(this.year, this.scopeParams()).subscribe({
      next: response => saveAs(response.body!, `hr-admin-operations-${this.year}.xlsx`),
      error: () => this.toast('error', 'Unable to export the compliance workbook.')
    });
  }

  cycleFor(row: AdminOperationsCalendarRow, month: number): AdminOperationsCycle | undefined {
    return row.months?.[month] ?? row.months?.[String(month) as unknown as number];
  }

  cycleCellStatus(row: AdminOperationsCalendarRow, month: number): string {
    const cycle = this.cycleFor(row, month);
    return cycle?.riskStatus || cycle?.workflowStatus || 'N/A';
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'COMPLETED' || status === 'VALID' || status === 'VERIFIED' || status === 'WAIVED') return 'success';
    if (status === 'IN_PROGRESS' || status === 'SUBMITTED') return 'info';
    if (status === 'DUE_SOON' || status === 'DUE' || status === 'EXPIRING_SOON' || status === 'PENDING_VERIFICATION') return 'warn';
    if (status === 'OVERDUE' || status === 'EXPIRED' || status === 'REJECTED') return 'danger';
    return 'secondary';
  }

  daysLabel(days?: number): string {
    if (days === undefined || days === null) return 'No expiry';
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    return `${days} days left`;
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private scopeParams(): AdminOperationsScopeParams {
    if (this.scope?.type === 'GROUP') return { companyGroupId: this.scope.companyGroupId };
    return { owningCompanyId: this.scope?.companyId };
  }

  private riskTone(value?: number): 'success' | 'danger' {
    return (value ?? 0) > 0 ? 'danger' : 'success';
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({ severity, summary: 'Admin Operations', detail });
  }
}
